// ============================================================
// VA DISCOUNT ENGINE  ·  va-discount.js  ·  Production v3
// ============================================================
// FIX LOG (v2 → v3)
// ─────────────────
// • Replaced ALL CSS attribute-selector queries that used
//   data-key (Shopify keys contain ":" which can mis-parse
//   in some browser querySelector implementations) with
//   Array.from().find() — 100% safe against special chars.
// • Added MutationObserver guard: if the theme's own cart JS
//   updates #cart-total, we immediately re-run updateTotals()
//   so VA rows always stay in sync.
// • Moved clearExistingDiscount() out of the remove handler
//   hot path — it caused a 200–400 ms delay that made the
//   UI look frozen. It now runs only at checkout time.
// • Added BOOT_VERSION constant so you can verify in the
//   console which version is actually running.
// ============================================================

(function () {
  'use strict';

  const BOOT_VERSION = 'VA-v3';
  const WORKER_URL   = 'https://hello-world.giri1-agt.workers.dev/';
  const GOLD_TAG     = 'gold_coin';
  // const VA_KEYS      = ['cartVAValue', 'cartVAvalue', 'CartVAValue'];
  const VA_KEYS = ['_cartVAValue', '_cartVAvalue', '_CartVAValue'];
  const DEBOUNCE_MS  = 350;

  console.log(`[VA] ${BOOT_VERSION} loading…`);

  // ── Tier table ────────────────────────────────────────────
  // cart.item_count = total quantity across all lines
  // function getDiscountPct(itemCount) {
  //   if (itemCount === 1) return 20;
  //   if (itemCount === 2) return 30;
  //   if (itemCount >= 3) return 50;
  //   return 0;
  // }

  // ── Money helpers ─────────────────────────────────────────
  function parseVA(raw) {
    if (!raw) return 0;
    const n = parseFloat(String(raw).replace(/[₹,\s]/g, ''));
    return isNaN(n) ? 0 : n;
  }

  function readVA(item) {
    const props = item.properties || {};
    for (const key of VA_KEYS) {
      if (props[key]) return parseVA(props[key]);
    }
    return 0;
  }

  function isGoldCoin(item) {
    return String(item.product_tags || item.tags || '').toLowerCase().includes(GOLD_TAG);
  }

  // Format paise → "₹1,16,964.00"
  function fmtMoney(paise) {
    return '₹' + (paise / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // Format ₹ float → "₹10,131.10"
  function fmtRS(rs) {
    return '₹' + Number(rs).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // ── Safe DOM finder ───────────────────────────────────────
  // Shopify item.key = "variantId:hash" — the colon can cause
  // issues in CSS attribute selectors in some environments.
  // We always use Array.from().find() instead of querySelector.
  function findByKey(selector, key, fallbackLine) {
    const all = Array.from(document.querySelectorAll(selector));
    // Prefer key match (stable)
    if (key) {
      const byKey = all.find(el => el.dataset.key === key);
      if (byKey) return byKey;
    }
    // Fallback: line index match
    return all.find(el => el.dataset.line === String(fallbackLine)) || null;
  }

  function findAllByKey(selector, key, fallbackLine) {
    const all = Array.from(document.querySelectorAll(selector));
    if (key) {
      const byKey = all.filter(el => el.dataset.key === key);
      if (byKey.length) return byKey;
    }
    return all.filter(el => el.dataset.line === String(fallbackLine));
  }

  // ── Shopify API ───────────────────────────────────────────
  async function getCart() {
    const res = await fetch('/cart.js', { cache: 'no-store' });
    if (!res.ok) throw new Error(`/cart.js ${res.status}`);
    return res.json();
  }

  async function clearExistingDiscount() {
    try {
      await fetch('/discount/clear', { method: 'GET' });
      await fetch('/cart/update.js', {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ discount: '' }),
      });
      console.log('[VA] Old discount cleared');
    } catch (e) {
      console.warn('[VA] clearDiscount (non-fatal):', e.message);
    }
  }

  // ── Core discount calculation ─────────────────────────────
  // Returns plain object — synchronous, no fetch.
  function calcDiscount(cart) {
    const pct = getDiscountPct(cart.item_count);
    let totalVADiscountRS  = 0;
    let finalTotalPaise    = 0;
    let totalOriginalPaise = 0;

    for (const item of cart.items) {
      const vaRS        = isGoldCoin(item) ? 0 : readVA(item);
      const discPerUnit = Math.round((vaRS * pct) / 100 * 100); // paise per unit
      const finalUnit   = item.original_price - discPerUnit;

      totalVADiscountRS  += (discPerUnit / 100) * item.quantity;
      finalTotalPaise    += finalUnit * item.quantity;
      totalOriginalPaise += item.original_price * item.quantity;

      console.log(
        `[VA] "${item.product_title}" qty=${item.quantity} va=₹${vaRS}` +
        ` disc${pct}%=₹${(discPerUnit/100).toFixed(2)}/unit`
      );
    }

    totalVADiscountRS = Math.round(totalVADiscountRS * 100) / 100;
    console.log(`[VA] Tier=${pct}% | Discount=${fmtRS(totalVADiscountRS)} | Final=${fmtMoney(finalTotalPaise)}`);

    return { pct, totalVADiscountRS, finalTotalPaise, totalOriginalPaise };
  }

  // ── Update totals rows only (no item rebuild) ─────────────
  // Safe to call multiple times — idempotent.
  function updateTotals(cart) {
    const { pct, totalVADiscountRS, finalTotalPaise, totalOriginalPaise } = calcDiscount(cart);

    // VA Discount label
    document.querySelectorAll('#va-discount-label').forEach(el => {
      el.textContent = `VA Discount (${pct}%)`;
    });

    // VA Discount amount
    document.querySelectorAll('#va-discount').forEach(el => {
      el.innerHTML = totalVADiscountRS > 0
        ? `<span style="color:#27ae60;font-weight:600">− ${fmtRS(totalVADiscountRS)}</span>`
        : '<span>₹0.00</span>';
    });

    // Original total (strikethrough if discount applies)
    document.querySelectorAll('#cart-total').forEach(el => {
      // GUARD: skip if this element is inside mobile footer
      // (that one shows the final price, not original)
      const inFooter = !!el.closest('.wd-cart-mobile-footer-wrapper');
      if (inFooter) {
        el.innerHTML = `<strong>${fmtMoney(finalTotalPaise)}</strong>`;
      } else {
        el.innerHTML = totalVADiscountRS > 0
          ? `<s style="color: #000000;font-size: 1em;">${fmtMoney(totalOriginalPaise)}</s>`
          : fmtMoney(totalOriginalPaise);
      }
    });

    // Final discounted total
    document.querySelectorAll('#final-total').forEach(el => {
      el.innerHTML =
        `<span style="color:#c00;font-weight:700">${fmtMoney(finalTotalPaise)}</span>`;
    });

    // Cache for checkout handler
    window._vaData = { pct, totalVADiscountRS, finalTotalPaise, uniqueItems: cart.item_count };
  }

  // ── Desktop row HTML ──────────────────────────────────────
  function desktopRowHTML(item, pct) {
    const vaRS       = isGoldCoin(item) ? 0 : readVA(item);
    const discPaise  = Math.round((vaRS * pct) / 100 * 100);
    const finalUnitP = item.original_price - discPaise;
    const origLineP  = item.original_price * item.quantity;
    const finalLineP = finalUnitP * item.quantity;

    const unitHTML = discPaise > 0
      ? `<s style="color:#aaa;font-size:.82em">${fmtMoney(item.original_price)}</s>
         <br><span style="color:#c00;font-weight:600">${fmtMoney(finalUnitP)}</span>`
      : `<span class="amount">${fmtMoney(item.original_price)}</span>`;

    const lineHTML = discPaise > 0
      ? `<s style="color:#aaa;font-size:.82em;display:block">${fmtMoney(origLineP)}</s>
         <span style="color:#c00;font-weight:600">${fmtMoney(finalLineP)}</span>`
      : fmtMoney(origLineP);

    const varHTML = item.variant_title && !item.variant_title.includes('Default')
      ? `<small style="display:block;color:#959595;">${item.variant_title}</small>` : '';

    // IMPORTANT: data-key is written as a data attribute but
    // we NEVER use it in a CSS selector — only via .dataset.key
    return `
      <tr class="cart-item" data-line="${item.line}" data-key="${item.key}">
        <td class="product-thumbnail" data-label="Product Name">
          <a href="${item.url}"><img src="${item.image}" alt="${item.title}" style="width:60px;"></a>
        </td>
        <td class="product-name-thumb" data-title="Product">
          <a href="${item.url}">${item.product_title}</a>${varHTML}
        </td>
        <td class="product-price" data-label="Product Price">${unitHTML}</td>
        <td class="product-quantity quantity-column" data-label="Quantity">
          <div class="quantity-selector">
            <button type="button" class="quantity-btn quantity-decrease"
              data-line="${item.line}" data-key="${item.key}" data-action="decrease">−</button>
            <input type="number" class="quantity-input js-qty__num"
              data-line="${item.line}" data-key="${item.key}"
              data-current-quantity="${item.quantity}"
              value="${item.quantity}" min="1" max="99">
            <button type="button" class="quantity-btn quantity-increase"
              data-line="${item.line}" data-key="${item.key}" data-action="increase">+</button>
          </div>
        </td>
        <td class="product-subtotal" data-label="Sub Total">
          <span class="amount item-total" data-line="${item.line}">${lineHTML}</span>
        </td>
        <td class="product-remove">
          <a class="remove-btn set-12-svg" href="#"
            data-line="${item.line}" data-key="${item.key}" data-action="remove">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" style="width:16px;height:16px;">
              <path d="M14 3h-3.53a3.07 3.07 0 00-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85
                A3.06 3.06 0 005.53 3H2a.5.5 0 000 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0
                00.5-.5V4H14a.5.5 0 000-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24
                .3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z"
                fill="currentColor"/>
              <path d="M6.55 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5z
                M9.45 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5z"
                fill="currentColor"/>
            </svg>
          </a>
        </td>
      </tr>`;
  }

  // ── Mobile card HTML ──────────────────────────────────────
  function mobileCardHTML(item, pct) {
    const vaRS       = isGoldCoin(item) ? 0 : readVA(item);
    const discPaise  = Math.round((vaRS * pct) / 100 * 100);
    const finalUnitP = item.original_price - discPaise;
    const origLineP  = item.original_price * item.quantity;
    const finalLineP = finalUnitP * item.quantity;

    const linePriceHTML = discPaise > 0
      ? `<s style="color:#aaa;font-size:.82em">${fmtMoney(origLineP)}</s>
         <strong style="color:#c00;display:block">${fmtMoney(finalLineP)}</strong>`
      : fmtMoney(origLineP);

    const varHTML = item.variant_title && !item.variant_title.includes('Default')
      ? `<small style="display:block;color:#959595;">${item.variant_title}</small>` : '';

    return `
      <div class="wd-cart-item" data-line="${item.line}" data-key="${item.key}">
        <div class="wd-cart-img">
          <a href="${item.url}"><img src="${item.image}" alt="${item.title}"></a>
        </div>
        <div class="wd-cart-content">
          <p><a href="${item.url}">${item.product_title}</a>${varHTML}</p>
          <div class="wd-cart-content-qty-wrapper">
            <div class="wd-cart-content-qty-inner">
              <div class="quantity-selector">
                <button type="button" class="quantity-btn quantity-decrease"
                  data-line="${item.line}" data-key="${item.key}" data-action="decrease">−</button>
                <input type="number" class="quantity-input js-qty__num"
                  data-line="${item.line}" data-key="${item.key}"
                  data-current-quantity="${item.quantity}"
                  value="${item.quantity}" min="1" max="99">
                <button type="button" class="quantity-btn quantity-increase"
                  data-line="${item.line}" data-key="${item.key}" data-action="increase">+</button>
              </div>
              <a class="remove-btn set-12-svg" href="#"
                data-line="${item.line}" data-key="${item.key}" data-action="remove">Remove</a>
            </div>
            <p><span class="amount item-total" data-line="${item.line}">${linePriceHTML}</span></p>
          </div>
        </div>
      </div>`;
  }

  // ── Full cart render ──────────────────────────────────────
  async function renderCart(cartData) {
    try {
      const cart = cartData || await getCart();

      if (cart.item_count === 0) {
        window.location.reload();
        return;
      }

      const { pct } = calcDiscount(cart);  // just for building HTML

      // Rebuild desktop tbody
      const tbody = document.querySelector('#cart-items tbody');
      if (tbody) {
        tbody.innerHTML = cart.items.map(item => desktopRowHTML(item, pct)).join('');
      }

      // Rebuild mobile list
      const mWrap = document.querySelector('.wd-cart-mobile-wrapper');
      if (mWrap) {
        mWrap.innerHTML = cart.items.map(item => mobileCardHTML(item, pct)).join('');
      }

      // Patch all totals rows
      updateTotals(cart);

      attachCheckoutListeners();

    } catch (err) {
      console.error('[VA] renderCart failed:', err);
      window.location.reload();
    }
  }

  // ── Debounce (per-item) ───────────────────────────────────
  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
  const _debounced = {};  // keyed by item.key

  // ── Cart API ──────────────────────────────────────────────
  async function changeQty(itemKey, lineIdx, qty) {
    // Prefer key-based (stable), fall back to line index
    const body = itemKey
      ? { id: itemKey, quantity: qty }
      : { line: lineIdx, quantity: qty };

    const res = await fetch('/cart/change.js', {
      method : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body   : JSON.stringify(body),
    });
    const data = await res.json();
    if (data.status >= 400) throw new Error(data.description || data.message);
    return data; // fresh cart
  }

  // ── Inline error bar ──────────────────────────────────────
  function showError(msg) {
    let bar = document.getElementById('va-error-bar');
    if (!bar) {
      bar = Object.assign(document.createElement('div'), { id: 'va-error-bar' });
      bar.style.cssText =
        'background:#ffeaea;color:#c00;padding:10px 16px;border-radius:6px;' +
        'margin-bottom:12px;font-size:14px;font-weight:600;display:none;';
      const ref = document.getElementById('cart-items');
      if (ref) ref.parentNode.insertBefore(bar, ref);
    }
    bar.textContent = msg;
    bar.style.display = 'block';
    clearTimeout(bar._t);
    bar._t = setTimeout(() => { bar.style.display = 'none'; }, 4000);
  }

  // ══════════════════════════════════════════════════════════
  // EVENT: Quantity buttons  (delegated — works after rebuild)
  // ══════════════════════════════════════════════════════════
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.quantity-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const action  = btn.dataset.action;                 // "increase" | "decrease"
    const itemKey = btn.dataset.key   || null;          // Shopify stable key
    const lineIdx = parseInt(btn.dataset.line, 10);    // Liquid loop index

    // Find the qty input using dataset property (NOT CSS selector with key)
    const input = findByKey('.quantity-input', itemKey, lineIdx);
    if (!input) {
      console.error('[VA] Could not find qty input for key:', itemKey, 'line:', lineIdx);
      return;
    }

    let qty = parseInt(input.value, 10) || 1;
    if (action === 'increase') qty = Math.min(qty + 1, 99);
    if (action === 'decrease') qty = Math.max(qty - 1, 1);
    input.value = qty;  // optimistic

    // Disable buttons (find without special-char selector)
    findAllByKey('.quantity-btn', itemKey, lineIdx).forEach(b => (b.disabled = true));

    // Debounce per item so rapid clicks → single API call
    const dKey = itemKey || String(lineIdx);
    if (!_debounced[dKey]) {
      _debounced[dKey] = debounce(async (key, line, q) => {
        try {
          const updatedCart = await changeQty(key, line, q);
          await renderCart(updatedCart);
        } catch (err) {
          console.error('[VA] Qty update error:', err);
          // Revert optimistic value
          const inp = findByKey('.quantity-input', key, line);
          if (inp) inp.value = parseInt(inp.dataset.currentQuantity) || 1;
          showError('Could not update quantity. Please try again.');
        } finally {
          findAllByKey('.quantity-btn', key, line).forEach(b => (b.disabled = false));
        }
      }, DEBOUNCE_MS);
    }

    _debounced[dKey](itemKey, lineIdx, qty);
  });

  // ══════════════════════════════════════════════════════════
  // EVENT: Remove buttons  (delegated)
  // ══════════════════════════════════════════════════════════
  document.addEventListener('click', async function (e) {
    const btn = e.target.closest('.remove-btn[data-action="remove"]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const itemKey = btn.dataset.key || null;
    const lineIdx = parseInt(btn.dataset.line, 10);

    // Fade out row (optimistic)
    findAllByKey('.cart-item, .wd-cart-item', itemKey, lineIdx)
      .forEach(el => Object.assign(el.style, { opacity: '.3', pointerEvents: 'none', transition: 'opacity .2s' }));

    try {
      await changeQty(itemKey, lineIdx, 0);
      await renderCart();          // fetch fresh cart — discount recalculates
    } catch (err) {
      console.error('[VA] Remove error:', err);
      findAllByKey('.cart-item, .wd-cart-item', itemKey, lineIdx)
        .forEach(el => (el.style.cssText = ''));
      showError('Could not remove item. Please try again.');
    }
  });

  // ══════════════════════════════════════════════════════════
  // Checkout intercept
  // ══════════════════════════════════════════════════════════
  async function handleCheckout(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    if (window._vaCheckoutInProgress) return;
    window._vaCheckoutInProgress = true;

    const overlay = document.getElementById('loading-overlay');
    if (overlay) overlay.style.display = 'flex';

    try {
      const cart = await getCart();
      const { pct, totalVADiscountRS } = calcDiscount(cart);

      await clearExistingDiscount();

      if (!totalVADiscountRS || totalVADiscountRS <= 0) {
        window.location.href = '/checkout';
        return;
      }

      const res = await fetch(WORKER_URL, {
        method : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ totalDiscountRS: totalVADiscountRS, vaItemCount: cart.item_count }),
      });

      if (!res.ok) throw new Error(`Worker ${res.status}`);
      const result = await res.json();

      window.location.href = result.code
        ? `/discount/${encodeURIComponent(result.code)}?redirect=/checkout`
        : '/checkout';

    } catch (err) {
      console.error('[VA] Checkout error:', err);
      window._vaCheckoutInProgress = false;
      if (overlay) overlay.style.display = 'none';
      window.location.href = '/checkout';
    }
  }

  function attachCheckoutListeners() {
    const selectors = [
      'a.checkout-button',
      'a[href="/checkout"]',
      '.wc-proceed-to-checkout a',
      '.wd-cart-mobile-footer-wrapper a',
      'button[name="checkout"]',
    ];
    let n = 0;
    selectors.forEach(sel =>
      document.querySelectorAll(sel).forEach(btn => {
        if (!btn.dataset.vaAttached) {
          btn.dataset.vaAttached = 'true';
          btn.addEventListener('click', handleCheckout, true);
          n++;
        }
      })
    );
    if (n) console.log(`[VA] Attached to ${n} checkout button(s)`);
  }

  // ══════════════════════════════════════════════════════════
  // MutationObserver guard
  // If the theme's own AJAX cart updates #cart-total, we
  // immediately re-run updateTotals() to keep VA rows in sync.
  // ══════════════════════════════════════════════════════════
  let _toGuard;
  function watchForThemeCartUpdates() {
    const targets = ['cart-total', 'cart-items'];
    targets.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      new MutationObserver(() => {
        // Debounce: theme may batch multiple DOM mutations
        clearTimeout(_toGuard);
        _toGuard = setTimeout(async () => {
          try {
            const cart = await getCart();
            updateTotals(cart);
            console.log('[VA] Re-synced after theme cart update');
          } catch (_) {}
        }, 200);
      }).observe(el, { childList: true, subtree: true, characterData: true });
    });
  }

  // ══════════════════════════════════════════════════════════
  // Boot
  // ══════════════════════════════════════════════════════════
  document.addEventListener('DOMContentLoaded', async function () {
    console.log(`[VA] ${BOOT_VERSION} DOM ready`);

    if (window.location.pathname.startsWith('/cart')) {
      await clearExistingDiscount();
    }

    try {
      const cart = await getCart();
      updateTotals(cart);
      console.log('[VA] Initial totals patched ✅');
    } catch (err) {
      console.error('[VA] Boot updateTotals failed:', err);
    }

    attachCheckoutListeners();
    watchForThemeCartUpdates();

    // Also watch for third-party checkout button injections
    new MutationObserver(() => attachCheckoutListeners())
      .observe(document.body, { childList: true, subtree: true });

    console.log(`[VA] ${BOOT_VERSION} ready ✅`);
  });

})();